import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUp } from "lucide-react";

// Simplified nav + footer for /blog and /admin routes.
// Handles cross-route section anchors by navigating home first.
export function PageNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const goToSection = (id) => (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-obsidian/85 backdrop-blur-md border-b border-stroke"
      data-testid="page-nav"
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="page-nav-logo" className="flex items-center group">
          <span className="inline-flex items-center justify-center transition-transform group-hover:rotate-[6deg]">
            <img src="/dc-logo.png" alt="Digital Centaur" width="80" height="80" className="w-10 h-10 md:w-11 md:h-11 object-contain" />
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8" data-testid="page-nav-links">
          <Link to="/" data-testid="page-nav-home" className="font-mono text-[11px] tracking-[0.24em] uppercase text-steel hover:text-ink transition-colors">
            <span className="text-laser/80 mr-2">01</span>Home
          </Link>
          <a href="/#experience" onClick={goToSection("experience")} data-testid="page-nav-experience" className="font-mono text-[11px] tracking-[0.24em] uppercase text-steel hover:text-ink transition-colors">
            <span className="text-laser/80 mr-2">02</span>Experience
          </a>
          <Link to="/blog" data-testid="page-nav-journal" className="font-mono text-[11px] tracking-[0.24em] uppercase text-ink hover:text-laser transition-colors">
            <span className="text-laser/80 mr-2">03</span>Journal
          </Link>
          <a href="/#contact" onClick={goToSection("contact")} data-testid="page-nav-contact" className="font-mono text-[11px] tracking-[0.24em] uppercase text-steel hover:text-ink transition-colors">
            <span className="text-laser/80 mr-2">04</span>Contact
          </a>
        </nav>
        <a
          href="/#contact"
          onClick={goToSection("contact")}
          data-testid="page-nav-cta"
          className="btn-sweep border border-stroke px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase text-ink"
        >
          Let&rsquo;s Talk
        </a>
      </div>
    </motion.header>
  );
}

export function PageFooter() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const year = new Date().getFullYear();
  return (
    <footer className="relative border-t border-stroke bg-obsidian mt-24" data-testid="page-footer">
      <div className="container-x py-10 flex flex-wrap items-center justify-between gap-4">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-steel">
          © {year} Digital Centaur · Himanshu Oberoi · Dubai
        </div>
        <button onClick={toTop} data-testid="page-footer-to-top" className="btn-sweep inline-flex items-center gap-2 border border-stroke text-ink px-4 py-3 font-mono text-[11px] tracking-[0.24em] uppercase">
          <ArrowUp size={14} strokeWidth={1.5} />
          Top
        </button>
      </div>
    </footer>
  );
}
